import { XMLHttpRequestInterceptor } from '@mswjs/interceptors/XMLHttpRequest';

import {
  MFAError,
  MFAErrorCodes,
  type MFAResponse,
  XHRMFAError,
} from '../../domain';
import { buildRequestFromFetchInput } from './buildRequestDataFromFetchInput';
import { EventHandler } from './EventHandler';
import { InterceptedRequest } from './InterceptedRequest';
import { buildPromiseHandler, type PromiseHandler } from './PromiseHandler';

enum LogScopes {
  none = 'none',
  root = 'MFAInterceptor',
  fetch = 'MFAInterceptor.fetch',
  xhr = 'MFAInterceptor.xhr',
  xhrSend = 'MFAInterceptor.xhr.send',
  xhrSendReadystatechange = 'MFAInterceptor.xhr.send.readystatechange',
  xhrSendOnload = 'MFAInterceptor.xhr.send.onload',
  xhrSet = 'MFAInterceptor.xhr.set',
  xhrNew = 'MFAInterceptor.xhr.new',
  cancel = 'MFAInterceptor.cancel',
}

const getDbg = (): typeof console.debug => {
  if ('describe' in globalThis && 'test' in globalThis) {
    return (...args) => {
      const strArgs = args.map((arg) => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return arg;
          }
        }
        return arg;
      });
      process.stdout.write(
        `${new Date().toISOString()}> ${strArgs.join(' ')}\n`,
      );
    };
  }
  // biome-ignore lint/suspicious/noConsole: handled
  return (...args) => console.debug(...args);
};
const dbg = getDbg();

const logger = {
  scopes: [LogScopes.none],
  setAllowedScopes(scopes: LogScopes[]) {
    this.scopes = scopes;
  },
  filterOutScopes(scopes: LogScopes[]) {
    this.scopes = this.scopes.filter((s) => !scopes.includes(s));
  },
  log(scope: LogScopes, ...args: Parameters<typeof console.debug>) {
    if (!this.scopes.includes(scope)) return;

    dbg(`[${scope}]`, ...args);
  },
};
logger.setAllowedScopes(Object.values(LogScopes));

// this type declaration is needed for the typescript version on connect which is older
// and doesn't have defined the cause property on Error
declare global {
  interface Error {
    cause?: unknown;
  }
}

export class MFAInterceptorError extends Error {
  constructor(
    msg: string,
    public override cause: Error,
  ) {
    super(msg);
  }
}

const MFA_MAIN_STATUS_CODE = 428;
const MFA_ERROR_STATUS_CODE = 412;

export class MFAInterceptor {
  private originalFetch!: typeof fetch;
  private xhrInterceptor!: XMLHttpRequestInterceptor;
  private MFARequireHandler: EventHandler<InterceptedRequest>;
  // Store correct blobs for receipt endpoints by URL
  private receiptBlobs = new Map<string, Blob>();

  constructor() {
    this.MFARequireHandler = new EventHandler<InterceptedRequest>();
  }

  public initialise() {
    this.interceptFetch();
    this.interceptXMLHttpRequest();
  }

  private interceptFetch() {
    this.originalFetch = fetch.bind(window);
    // @ts-expect-error // Ignore TS error about fetch being readonly
    // biome-ignore lint/suspicious/noGlobalAssign: the whole point of this is to override fetch
    fetch = (
      input: Parameters<Window['fetch']>[0],
      init?: Parameters<Window['fetch']>[1],
    ): Promise<Response> => {
      return new Promise<Response>((resolve, reject) => {
        const interceptedRequest = new InterceptedRequest(
          buildRequestFromFetchInput(input, init),
          () => {
            logger.log(LogScopes.cancel, 'Request cancelled by user');
            reject(
              new MFAError(
                'User cancelled the MFA request',
                MFAErrorCodes.USER_CANCELLED,
              ),
            );
          },
        );

        const promiseHandler = buildPromiseHandler(resolve, reject);
        try {
          this.originalFetch(input, init)
            .then(async (response) => {
              if (
                [MFA_MAIN_STATUS_CODE, MFA_ERROR_STATUS_CODE].includes(
                  response.status,
                )
              ) {
                return this.handleMFAResponse({
                  interceptedRequest,
                  promiseHandler,
                  response,
                });
              }

              return resolve(response);
            })
            .catch((error) => {
              reject(error);
            });
          // .catch(reject);
        } catch (e) {
          logger.log(
            LogScopes.fetch,
            'Error in MFAInterceptor fetch override',
            e,
          );
          console.warn(
            '[MFAInterceptor] Error in fetch override calling original fetch with',
            input,
            init,
            'Reason:',
            e,
          );
          throw e;
        }
      });
    };
  }

  private interceptXMLHttpRequest() {
    this.xhrInterceptor = new XMLHttpRequestInterceptor();
    this.xhrInterceptor.apply();

    this.xhrInterceptor.on('request', async ({ request, controller }) => {
      const clonedRequest = request.clone();

      try {
        const retRes = await fetch(clonedRequest);
        // Check if this is a binary receipt endpoint that needs special handling
        const url = new URL(request.url);
        const isReceiptEndpoint = url.pathname.includes('/receipt/');

        if (isReceiptEndpoint) {
          await this.handleBinaryResponse(request, retRes, controller);
          return;
        }

        controller.respondWith(retRes);
      } catch (e) {
        if (MFAError.isMFAError(e)) {
          const xx = new XHRMFAError(e);
          const jsonResponse = {
            ...xx,
            message: xx.message,
            name: xx.name,
            code: xx.code,
            errors: xx.errors,
            additionalData: xx.additionalData,
          };
          logger.log(
            LogScopes.cancel,
            'XHR Responding with MFA OTPError',
            jsonResponse,
          );
          throw jsonResponse;
        }
        throw e;
      }
    });
  }

  // Handles binary responses (e.g., PDF receipts) by storing the correct blob
  // before the XMLHttpRequestInterceptor corrupts it during conversion.
  private async handleBinaryResponse(
    request: Request,
    response: Response,
    controller: { respondWith: (response: Response) => void },
  ): Promise<void> {
    if (response.bodyUsed) {
      controller.respondWith(response);
      return;
    }

    const correctBlob = await response.blob();
    this.receiptBlobs.set(request.url, correctBlob);

    const blobResponse = new Response(correctBlob, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });

    controller.respondWith(blobResponse);
  }

  private async handleMFAResponse({
    response,
    interceptedRequest,
    promiseHandler,
  }: {
    response: Response;
    interceptedRequest: InterceptedRequest;
    promiseHandler: PromiseHandler;
  }) {
    const responseJson = await this.getResponseJson(response);
    const expiryMins = responseJson.expiryMins;
    const pendingId = responseJson.pendingId;
    const errorCode = this.getErrorCodeFromResponse(
      response.status,
      responseJson,
    );
    interceptedRequest.setPendingId(pendingId);
    interceptedRequest.setExpiryMins(expiryMins);
    interceptedRequest.setError(errorCode);

    this.next(interceptedRequest, promiseHandler);
  }

  private async getResponseJson(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch (e) {
      throw new MFAInterceptorError(
        'Unable to parse MFA JSON response.',
        e as Error,
      );
    }
  }

  private getErrorCodeFromResponse(
    responseStatus: number,
    responseJson: MFAResponse,
  ): MFAErrorCodes | null {
    if (responseStatus !== MFA_ERROR_STATUS_CODE) return null;

    return responseJson.status as MFAErrorCodes;
  }

  private next(
    interceptedRequest: InterceptedRequest,
    promiseHandler: PromiseHandler,
  ) {
    interceptedRequest.onContinue((_challengeCode) => {
      fetch(interceptedRequest.getRequest())
        .then(promiseHandler.resolve)
        .catch(promiseHandler.reject);
    });
    this.triggerMFARequire(interceptedRequest);
  }

  public stop() {
    this.stopFetch();
    this.stopXMLHttpRequest();
    this.MFARequireHandler.clearListeners();
  }
  private stopFetch() {
    if (!this.originalFetch) return;
    // @ts-expect-error // Ignore TS error about fetch being readonly
    // biome-ignore lint/suspicious/noGlobalAssign: the whole point of this is to override fetch
    fetch = this.originalFetch;
  }
  private stopXMLHttpRequest() {
    this.xhrInterceptor.dispose();
  }

  public onMFARequire(
    callback: (interceptedRequest: InterceptedRequest) => void,
  ) {
    this.MFARequireHandler.addListener(callback);
  }
  private triggerMFARequire(interceptedRequest: InterceptedRequest) {
    this.MFARequireHandler.trigger(interceptedRequest);
  }

  public getReceiptBlob(url: string): Blob | undefined {
    return this.receiptBlobs.get(url);
  }

  public clearReceiptBlob(url: string): void {
    this.receiptBlobs.delete(url);
  }
}

export const mfaInterceptor = new MFAInterceptor();
